import {
  Box,
  ButtonProps,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridAddIcon, GridDeleteIcon } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { ReactComponent as Play } from "../../assets/icons/play.svg";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TimerOffIcon from "@mui/icons-material/TimerOff";
import { COLORS } from "../../assets/theme";
import Actions from "../../components/common/Actions";
import CustomDateRangePicker from "../../components/common/CustomDateRangePicker";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import SummaryStats from "../../components/SummaryStats";
import ToastNotification from "../../components/ToastNotification";
import config from "../../config.json";
import { fetchClients, getCustomerOptions } from "../../services/ClientService";
import { getOperatorCountryNameOptions } from "../../services/CountryService";
import {
  addMessageReSubmissionApi,
  addProvider,
  CheckMessageReSubmissionAPI,
  deleteMessageReSubmissionsApi,
  DlrStatus,
  editMessageReSubmissionApi,
  exportMessageReSubmission,
  fetchMessageReSubmission,
  fetchMessageReSubmissionHistory,
  getMessageReSubmissionById,
  messageReSubmissionBulkApi,
  MessageReSubmissionSummary,
  MessageReSubmissionType,
  Status,
  useMessageReSubmissionQuery,
  useMessageReSubmissionsQuery,
} from "../../services/MessageReSubmissionService";
import {
  fetchOperator,
  getOperatorNameOptions,
} from "../../services/OperatorsService";
import {
  fetchProviders,
  getProviderOptions,
} from "../../services/ProviderService";
import {
  DateRange,
  IField,
  IPageable,
  Option,
  Params,
} from "../../types/table/interfaces.type";
import {
  loadCountriesOptions,
  loadOperatorsByCountry,
  pageAsOptions,
} from "../../utils/commonDataUtils";
import History from "../../components/History";
import AutoComplete, {
  IAutoCompleteProps,
} from "../../components/common/AutoComplete";
import { hasJwtPermission } from "../../services/ServicesHelpers";

const spin = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}`;

const pulse = `
@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}`;

const styleElement = document.createElement("style");
styleElement.type = "text/css";
styleElement.appendChild(document.createTextNode(spin + pulse));
document.head.appendChild(styleElement);

const MessageReSubmissionPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [appliedFilters, setAppliedFilters] = useState<
    Params<MessageReSubmissionType & DateRange>
  >({
    search: "",
  });

  const [providersOptions, setProvidersOptions] = useState<Option[]>([]);
  const [countriesOptions, setCountriesOptions] = useState<Option[]>([]);
  const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
  const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
  const [clientOptions, setClientOptions] = useState<Option[]>([]);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [playingId, setPlayingId] = useState<string | number | null>(null);
  const [activeTestingId, setActiveTestingId] = useState<string | null>(null);
  const [submissionStatus, setSubmissionStatus] = useState<
    Record<string, "PENDING" | "DONE" | "FAILED" | "ERROR" | "EXPIRED" | undefined>
  >({});

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const [filters, setFilters] = useState<
    Params<MessageReSubmissionType & DateRange & { countryId: string }>
  >({
    search: "",
    customerId: "",
    providerId: "",
    status: "",
    dlrStatus: "",
  });
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [filters]);

  const dialogRef = useRef<MessageReSubmissionDialogRef>(null);

  const handleOpenDialog = (mode: "add" | "edit" | "history", id?: string) => {
    dialogRef.current?.openDialog(mode, id);
  };
  const handleOpenExecuteDialog = () => {
    dialogRef.current?.openExecuteDialog();
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const countriesAsOptions = await loadCountriesOptions();
        setCountriesOptions(countriesAsOptions);

        const providerResponse = await fetchProviders({
          size: config.MAX_PAGE_SIZE,
        });
        const providersAsOptions = providerResponse.content.map((provider) => ({
          label: provider.name,
          value: provider.id,
        }));
        setProvidersOptions(providersAsOptions);

        const clientPage = await fetchClients({
          size: config.MAX_PAGE_SIZE,
        });
        const clientAsOptions = pageAsOptions(clientPage);
        setClientOptions(clientAsOptions);
      } catch (error: any) {
        console.error("Error loading initial data:", error);
      }
    };

    loadInitialData();
  }, []);

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const onRowSelect = (row: MessageReSubmissionType, checked: boolean) => {
    setSelectedRowIds((prev) =>
      checked
        ? [...prev, String(row.id)]
        : prev.filter((id) => id !== String(row.id)),
    );
  };

  const isRowSelected = (row: MessageReSubmissionType) =>
    selectedRowIds.includes(String(row.id));

  const handleOpenDeleteDialog = () => {
    if (selectedRowIds.length === 0) {
      ToastNotification.warning(
        "Please select at least one re-submission to delete.",
      );
      return;
    }
    setPendingDeleteIds([...selectedRowIds]);
    setDeleteDialogOpen(true);
  };

  const handleOpenDeleteDialogForRow = (id: string | number) => {
    setPendingDeleteIds([String(id)]);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPendingDeleteIds([]);
  };

  const handleDeleteMessageReSubmissions = async () => {
    if (pendingDeleteIds.length === 0) return;
    setIsDeleting(true);
    try {
      await deleteMessageReSubmissionsApi(pendingDeleteIds);
      ToastNotification.success(
        "Message re-submission(s) deleted successfully!",
      );
      setSelectedRowIds((prev) =>
        prev.filter((id) => !pendingDeleteIds.includes(id)),
      );
      handleCloseDeleteDialog();
      setAppliedFilters((prev) => ({ ...prev }));
      queryClient.invalidateQueries();
      queryClient.invalidateQueries({ queryKey: ["messageRe"] });
    } catch (error: any) {
      ToastNotification.error(
        error?.message || "Failed to delete message re-submission(s).",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      customerId: filters.customerId,
      providerId: filters.providerId,
      operatorId: filters.operatorId,
      status: filters.status,
      dlrStatus: filters.dlrStatus,
    }));
    setSelectedRowIds([]);
  };

  const handleCountryChange = async (
    value: string | number | (string | number)[],
  ) => {
    const selectedCountry = Array.isArray(value) ? value[0] : value;
    if (selectedCountry) {
      try {
        const countryId = Number(selectedCountry);
        if (isNaN(countryId)) {
          throw new Error("Invalid country ID");
        }
        setIsOperatorDisabled(true);
        const ops = await loadOperatorsByCountry(countryId);
        setOperatorsByCountry(ops);
      } catch (error: any) {
        console.error("Error fetching operators for selected country:", error);
        setOperatorsByCountry([]);
      } finally {
        setIsOperatorDisabled(false);
      }
    } else {
      setOperatorsByCountry([]);
      setIsOperatorDisabled(false);
    }
  };

  const actions = Actions({
    onPlay: {
      handler: async (id: string | number) => {
        setPlayingId(id);
        try {
          console.log(`Play action triggered for ID: ${id}`);
          setSelectedId(String(id));
          handleOpenExecuteDialog();
        } catch (error: any) {
          ToastNotification.error(
            error?.message || "Failed to execute testing session.",
          );
          setPlayingId(null);
          setActiveTestingId(null);
        }
      },
      isDisabled: (row) =>
        row.resubmissionStatus === "DONE" ||
        row.resubmissionStatus === "PENDING" ||
        row.resubmissionStatus === "EXPIRED" ||
        !hasJwtPermission("EXECUTE_RESUBMISSION"),
      icon: (row) => {
        const currentStatus =
          submissionStatus[String(row.id)] || row.resubmissionStatus;

        if (currentStatus === "PENDING") {
          return (
            <Tooltip title="Pending">
              <HourglassEmptyIcon
                sx={{
                  color: COLORS.GOLD,
                  animation: `spin 2s linear infinite`,
                }}
              />
            </Tooltip>
          );
        }
        if (currentStatus === "DONE") {
          return (
            <Tooltip title="Executed">
              <CheckCircleIcon
                sx={{
                  color: COLORS.GOLD,
                  animation: `pulse 1.5s ease-in-out`,
                }}
              />
            </Tooltip>
          );
        }
        if (currentStatus === "EXPIRED") {
          return (
            <Tooltip title="Expired">
              <TimerOffIcon
                sx={{
                  color: "#9E9E9E",
                }}
              />
            </Tooltip>
          );
        }
        if (currentStatus === "ERROR" || currentStatus === "FAILED") {
          return (
            <Tooltip title="Error - Click to retry">
              <Play
                style={{
                  marginTop: 3,
                  width: 23,
                  color: hasJwtPermission("EXECUTE_RESUBMISSION")
                    ? "#FF0000"
                    : "#D3D3D3",
                }}
              />
            </Tooltip>
          );
        }
        return (
          <Tooltip title="Execute">
            <Play
              style={{
                marginTop: 3,
                width: 23,
                color: hasJwtPermission("EXECUTE_RESUBMISSION")
                  ? COLORS.GOLD
                  : "#D3D3D3",
              }}
            />
          </Tooltip>
        );
      },
    },
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
        setSelectedId(String(id));
      },
      isDisabled: !hasJwtPermission("GET_RESUBMISSION"),
    },
    onViewHistory: {
      // isDisabled: true,
      handler: async (id: string | number) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_RESUBMISSIONS_HISTORY"),
    },
    onDelete: {
      handler: async (id: string | number) => {
        handleOpenDeleteDialogForRow(id);
      },
      isDisabled: !hasJwtPermission("DELETE_RESUBMISSION"),
    },
  });

  const statusOptions = Object.entries(Status)
    .filter(([key, value]) => isNaN(Number(key)))
    .map(([key, value]) => ({
      label: key,
      value: String(key),
    }));

  const dlrStatusOptions = Object.entries(DlrStatus)
    .filter(([key, value]) => isNaN(Number(key)))
    .map(([key, value]) => ({
      label: key,
      value: String(key),
    }));

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Customer",
      value: filters.customerId ?? "",
      options: clientOptions,
      clearable: true,
      onChange: (value: string | number | (string | number)[]) => {
        const selectedCustomerId = clientOptions.find(
          (option) => option.value === value,
        );

        setFilters((prev) => ({
          ...prev,
          customerId: selectedCustomerId
            ? String(selectedCustomerId.value)
            : "",
        }));
      },
    },

    {
      label: "Provider",
      value: filters.providerId ?? "",
      options: providersOptions,
      clearable: true,
      placeholder: "Select Provider",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedProvider = providersOptions.find(
          (option) => option.value === value,
        );

        setFilters((prev) => ({
          ...prev,
          providerId: selectedProvider ? String(selectedProvider.value) : "",
        }));
      },
    },
    {
      label: "Country",
      value: Number(filters.countryId) ?? "",
      options: countriesOptions,
      clearable: true,
      placeholder: "Select Country",
      onChange: async (value: string | number | (string | number)[]) => {
        const selectedCountry = countriesOptions.find(
          (option) => option.value === value,
        );

        setFilters((prev) => ({
          ...prev,
          countryId: String(selectedCountry?.value),
          operatorId: undefined,
        }));

        if (selectedCountry) {
          try {
            setIsOperatorDisabled(true);
            const ops = await loadOperatorsByCountry(
              Number(selectedCountry.value),
            );
            setOperatorsByCountry(ops);
          } catch (error: any) {
            console.error("Error loading operators:", error);
            setOperatorsByCountry([]);
          } finally {
            setIsOperatorDisabled(false);
          }
        }

        try {
          await handleCountryChange(value);
        } catch (error: any) {
          console.error("Error in handleCountryChange:", error);
        }
      },
    },
    {
      label: "Operator",
      value: filters.operatorId ?? "",
      options: operatorsByCountry,
      disabled: isOperatorDisabled,
      clearable: true,
      placeholder: "Select Operator",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOperator = operatorsByCountry.find(
          (option) => option.value === value,
        );
        setFilters((prev) => ({
          ...prev,
          operatorId: selectedOperator
            ? Number(selectedOperator.value)
            : undefined,
        }));
      },
    },
    {
      label: "Status",
      value: filters.status ?? "",
      options: statusOptions,
      clearable: true,
      placeholder: "Select CDR Status",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedStatus = Object.keys(Status).find((key) => key === value);
        setFilters((prev) => ({
          ...prev,
          status: selectedStatus ?? undefined,
        }));
      },
    },
    {
      label: "DLR Status",
      value: filters.dlrStatus ?? "",
      options: dlrStatusOptions,
      clearable: true,
      placeholder: "Select CDR Status",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedStatus = Object.keys(DlrStatus).find(
          (key) => key === value,
        );
        setFilters((prev) => ({
          ...prev,
          dlrStatus: selectedStatus ?? undefined,
        }));
      },
    },
  ];
  const buttons = [
    {
      label: "Search",
      variant: "contained" as "contained",
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_RESUBMISSIONS",
    },
  ];

  const fields: IField<MessageReSubmissionType>[] = [
    { key: "name", header: "Name" },
    {
      key: "customerId",
      header: "Customer",
      options: (t) => getCustomerOptions(t.customerId),
    },
    {
      key: "providerId",
      header: "Provider",
      options: (t) => getProviderOptions(t.providerId),
    },
    {
      key: "operatorId",
      header: "Country",
      options: (t) => getOperatorCountryNameOptions(t.operatorId),
    },
    {
      key: "operatorId",
      header: "Operator",
      options: (t) => getOperatorNameOptions(t.operatorId),
    },

    { key: "createdDate", header: "Created Date" },
    { key: "createdBy", header: "User" },
  ];

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <Box sx={{ mb: 3 }}>
        <SearchForm
          dateComponent={
            <CustomDateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
            />
          }
          autoCompletes={autoCompletes}
          buttons={buttons}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Message Re-Submission List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginTop={1}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search ?? ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              permission: "POST_RESUBMISSION",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              variant: "outlined" as ButtonProps["variant"],
              startIcon: <GridDeleteIcon />,
              onClick: handleOpenDeleteDialog,
              textColor: COLORS.iconColor,
              permission: "DELETE_RESUBMISSION",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,

              onClick: async () => {
                try {
                  await exportMessageReSubmission(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again.",
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_RESUBMISSIONS_CSV",
            },
          ]}
        />

        <CustomTable<MessageReSubmissionType>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useMessageReSubmissionsQuery}
          actions={actions}
          checkboxSelect={true}
          onRowSelect={onRowSelect}
          isRowSelected={isRowSelected}
          shadeBg="#D9D9D9"
          shadeRow={(row) => {
            const currentStatus =
              submissionStatus[String(row.id)] || row.resubmissionStatus;
            return Boolean(currentStatus === "DONE" || currentStatus === "EXPIRED");
          }}
          sortableFields={["name", "createdDate", "createdBy"]}
        />
      </Box>

      <MessageReSubmissionDialog
        ref={dialogRef}
        onRefresh={async () => {
          setAppliedFilters((prev) => ({ ...prev }));

          if (selectedId) {
            try {
              const resubmission = await getMessageReSubmissionById(
                String(selectedId),
              );
              if (resubmission) {
                setSubmissionStatus((prev) => ({
                  ...prev,
                  [selectedId]: resubmission.resubmissionStatus as any,
                }));
              }
            } catch (error) {
              console.error("Error updating submission status:", error);
            }
          }
          queryClient.invalidateQueries();
          queryClient.invalidateQueries({ queryKey: ["messageRe"] });
          queryClient.refetchQueries({ queryKey: ["messageRe"] });
        }}
        countriesOptions={countriesOptions}
        loadOperatorsByCountry={loadOperatorsByCountry}
        statusOptions={statusOptions}
        dlrStatusOptions={dlrStatusOptions}
        selectedId={selectedId}
        clientOptions={clientOptions}
        providersOptions={providersOptions}
        submissionStatus={submissionStatus}
        setSubmissionStatus={setSubmissionStatus}
        setPlayingId={setPlayingId}
        applyFilters={appliedFilters}
      />

      <CustomDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        mode="delete"
        title="Delete Message Re-Submission"
        fields={[
          {
            type: "custom",
            component: (
              <Box>
                <Typography>
                  Are you sure you want to delete {pendingDeleteIds.length}{" "}
                  selected re-submission
                  {pendingDeleteIds.length === 1 ? "" : "s"}?
                </Typography>
              </Box>
            ),
          },
        ]}
        buttons={[
          {
            label: isDeleting ? "Deleting..." : "Delete",
            variant: "contained",
            bgColor: COLORS.GOLD,
            textColor: COLORS.iconColor,
            borderRadius: "12px",
            onClick: handleDeleteMessageReSubmissions,
            disabled:
              isDeleting || !hasJwtPermission("DELETE_RESUBMISSION"),
          },
        ]}
      />
    </Box>
  );
};

interface DialogData extends Omit<MessageReSubmissionType, "operatorId"> {
  operatorId?: number | number[];
  countryId: number;
}

function areAllOperatorsSelected(
  selected: number[] | undefined,
  operatorOptions: Option[],
): boolean {
  if (!operatorOptions.length) return false;
  const optionIds = new Set(operatorOptions.map((o) => Number(o.value)));
  const selectedSet = new Set(selected ?? []);
  return (
    selectedSet.size === optionIds.size &&
    [...optionIds].every((id) => selectedSet.has(id))
  );
}

function checkSummaryNumber(value: unknown): number {
  if (value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function roundCheckSummary(s: MessageReSubmissionSummary): MessageReSubmissionSummary {
  const totalCost = checkSummaryNumber(s.totalCost);
  const totalRate = checkSummaryNumber(s.totalRate);
  return {
    count: Math.round(checkSummaryNumber(s.count)),
    delivered: Math.round(checkSummaryNumber(s.delivered)),
    totalCost: Number(totalCost.toFixed(6)),
    totalRate: Number(totalRate.toFixed(6)),
  };
}

export interface MessageReSubmissionDialogRef {
  openDialog: (mode: "add" | "edit" | "history", id?: string) => void;
  openExecuteDialog: () => void;
}

interface MessageReSubmissionDialogProps {
  onRefresh: () => void;
  countriesOptions: Option[];
  statusOptions: Option[];
  dlrStatusOptions: Option[];
  clientOptions: Option[];
  applyFilters: Params<MessageReSubmissionType & DateRange>;
  providersOptions: Option[];
  selectedId: string | undefined;
  submissionStatus: Record<
    string,
    "PENDING" | "DONE" | "FAILED" | "ERROR" | "EXPIRED" | undefined
  >;
  setSubmissionStatus: React.Dispatch<
    React.SetStateAction<
      Record<string, "PENDING" | "DONE" | "FAILED" | "ERROR" | "EXPIRED" | undefined>
    >
  >;
  setPlayingId: (id: string | number | null) => void;
  loadOperatorsByCountry: (countryId: number) => Promise<Option[]>;
}

const MessageReSubmissionDialog = forwardRef<
  MessageReSubmissionDialogRef,
  MessageReSubmissionDialogProps
>(
  (
    {
      onRefresh,
      countriesOptions,
      dlrStatusOptions,
      clientOptions,
      statusOptions,
      applyFilters,
      selectedId,
      providersOptions,
      setPlayingId,
      submissionStatus,
      setSubmissionStatus,
    },
    ref,
  ) => {
    const queryClient = useQueryClient();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<"add" | "edit" | "history">(
      "add",
    );
    const [dialogData, setDialogData] = useState<Partial<DialogData>>({});
    const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
    const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [isExecuteMode, setIsExecuteMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string | undefined>();
    const [summary, setSummary] = useState<{
      count: number;
      delivered: number;
      totalRate: number;
      totalCost?: number;
    }>({
      count: 0,
      delivered: 0,
      totalRate: 0,
      totalCost: 0,
    });
    const [showSummary, setShowSummary] = useState(false);
    const validateField = (field: string, value: string) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: !value,
      }));
    };
    const getDefaultDialogData = (): Partial<
      MessageReSubmissionType & { countryId: number }
    > => ({
      name: "",
      customerId: "",
      providerId: "",
      operatorId: undefined,
      status: "",
      dlrStatus: "",
      fromDate: undefined,
      toDate: undefined,
      countryId: undefined,
    });
    const populateDialogData = async (id: string) => {
      const record = await fetchMessageReSubmission(id);
      let countryId: number | undefined;

      if (record.operatorId) {
        try {
          const { countryId: selectedCountry } = await fetchOperator(
            record.operatorId,
          );

          if (selectedCountry) {
            countryId = Number(selectedCountry);
            setIsOperatorDisabled(true);
            const operators = await loadOperatorsByCountry(countryId);
            setOperatorsByCountry(operators);
          }
        } catch (error: any) {
          console.error(
            "Error loading country or operators by operator ID:",
            error,
          );
        } finally {
          setIsOperatorDisabled(false);
        }
      }

      setDialogData({
        ...getDefaultDialogData(),
        name: record.name,
        customerId: record.customerId,
        providerId: record.providerId,
        operatorId: record.operatorId ? [record.operatorId] : [],
        status: record.status,
        postTranslatedSource: record.postTranslatedSource,
        countryId: countryId ?? 0,
        resubmissionStatus: record.resubmissionStatus,
        dlrStatus: record.dlrStatus,
        fromDate: record.fromDate,
        toDate: record.toDate,
        isSubmitted: record.isSubmitted ?? false,
        version: record.version,
      });
    };

    useImperativeHandle(ref, () => ({
      openDialog: async (mode: "add" | "edit" | "history", id?: string) => {
        setIsExecuteMode(false);
        setDialogMode(mode);
        setSelectedIds(id);
        setShowSummary(false);
        const defaultData = getDefaultDialogData();
        if (mode === "edit" && id) {
          try {
            await populateDialogData(id);
          } catch (error: any) {
            console.error("Failed to load Message Re-submission details:", error);
            ToastNotification.error(
              "Failed to fetch Message Re‑submission details.",
            );
            return;
          }
        } else {
          setDialogData(defaultData);
        }
        setDialogOpen(true);
      },
      openExecuteDialog: () => {
        setIsExecuteMode(true);
        setDialogMode("add");
        setSelectedIds(selectedId);
        setShowSummary(false);
        void (async () => {
          if (selectedId) {
            try {
              await populateDialogData(selectedId);
            } catch (error: any) {
              console.error("Failed to load Message Re-submission details:", error);
              ToastNotification.error(
                "Failed to fetch Message Re‑submission details.",
              );
              return;
            }
          } else {
            setDialogData(getDefaultDialogData());
          }
          setDialogOpen(true);
        })();
      },
    }));

    useEffect(() => {
      const validateFields = () => {
        const requiredFields: (keyof typeof dialogData)[] = [
          "dlrStatus",
          "fromDate",
          "toDate",
          "status",
          "operatorId",
          "providerId",
        ];

        const isAnyFieldEmpty = requiredFields.some(
          (field) => !dialogData[field],
        );

        setIsSaveDisabled(isAnyFieldEmpty);
      };

      validateFields();
    }, [dialogData]);

    const resetDialogData = () => {
      setDialogData({});
      setOperatorsByCountry([]);
      onRefresh();
      setIsSaveDisabled(true);
      setIsOperatorDisabled(true);
      setDialogOpen(false);
      setShowSummary(false);
    };

    const { data: polledItem } = useMessageReSubmissionQuery(selectedId ?? "");

    useEffect(() => {
      if (!polledItem || !selectedId) return;
      const status = polledItem.resubmissionStatus as
        | "PENDING"
        | "DONE"
        | "FAILED"
        | "ERROR"
        | "EXPIRED"
        | undefined;

      setSubmissionStatus((prev) => ({ ...prev, [selectedId]: status }));

      if (status === "PENDING") {
        setPlayingId(selectedId);
        return;
      }

      if (status === "DONE" || status === "ERROR" || status === "FAILED" || status === "EXPIRED") {
        setPlayingId(null);
        if (status === "DONE") {
          ToastNotification.success("Re-submission completed");
        } else if (status === "EXPIRED") {
          ToastNotification.warning("Re-submission expired");
        } else {
          ToastNotification.error(`Re-submission ${status}`);
        }
        onRefresh();
      }
    }, [polledItem, selectedId, setPlayingId, setSubmissionStatus]);

    const handleExecute = async () => {
      const idToProcess = String(selectedId);
      const providerId = dialogData.providerId ?? "";

      try {
        resetDialogData();
        setDialogOpen(false);
        await addProvider(idToProcess, providerId);
        ToastNotification.success("Re-submission execution initiated");

        setSubmissionStatus((prev) => ({
          ...prev,
          [idToProcess]: "PENDING",
        }));
        setPlayingId(idToProcess);
        resetDialogData();
      } catch (error: any) {
        console.error(`Error executing ID ${idToProcess}:`, error);
        ToastNotification.error(error?.message || "Failed to execute.");
        setPlayingId(null);
      }
    };

    const handleCountryChange = async (
      value: string | number | (string | number)[],
    ) => {
      const selectedCountry = Array.isArray(value) ? value[0] : value;
      setDialogData((prev) => ({
        ...prev,
        countryId: Number(selectedCountry),
        operatorId: undefined,
      }));

      if (selectedCountry) {
        try {
          const countryId = Number(selectedCountry);
          if (isNaN(countryId)) {
            throw new Error("Invalid country ID");
          }

          setIsOperatorDisabled(false);
          const operators = await loadOperatorsByCountry(countryId);
          setOperatorsByCountry(operators);
        } catch (error: any) {
          ToastNotification.error(
            "Error fetching operators for selected country.",
          );
          setOperatorsByCountry([]);
        }
      } else {
        setOperatorsByCountry([]);
        setIsOperatorDisabled(true);
      }
    };

    const addMessageReSubmission = async (
      data: Partial<MessageReSubmissionType>,
    ) => {
      try {
        const { operatorId, ...rest } = data;
        const baseData: Partial<MessageReSubmissionType> = {
          ...rest,
          fromDate: dialogData.fromDate,
          toDate: dialogData.toDate,
        };

        if (Array.isArray(operatorId) && operatorId.length > 1) {
          const payloads = operatorId.map((id, index) => ({
            ...baseData,
            operatorId: id,
            name: data.name ? `${data.name}_${index + 1}` : undefined,
          }));

          await messageReSubmissionBulkApi(payloads);
        } else {
          const payload: Partial<MessageReSubmissionType> = {
            ...baseData,
            operatorId: Array.isArray(operatorId) ? operatorId[0] : operatorId,
          };
          await addMessageReSubmissionApi(payload);
        }

        setDialogOpen(false);
        resetDialogData();
        onRefresh();
        ToastNotification.success("Message Re-submission added successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const handleEditMessageReSubmission = async (
      data: Partial<MessageReSubmissionType>,
    ) => {
      if (!selectedId) {
        ToastNotification.warning("Message Re-submission ID is missing.");
        return;
      }

      try {
        const { operatorId, id, ...rest } = data;

        const version = (
          data.version !== undefined ? data.version : dialogData.version
        ) as number | undefined;
        const baseData: Partial<MessageReSubmissionType> = {
          ...rest,
          fromDate: dialogData.fromDate,
          toDate: dialogData.toDate,
          isSubmitted: false,
          version,
        };

        if (Array.isArray(operatorId) && operatorId.length > 1) {
          const payloads = operatorId.map((opId, index) => ({
            ...baseData,
            operatorId: opId,
            id: typeof id === "string" ? id : selectedId,
            name: data.name ? `${data.name}_${index + 1}` : undefined,
          }));
          await messageReSubmissionBulkApi(payloads);
        } else {
          const payload: Partial<MessageReSubmissionType> = {
            ...baseData,
            operatorId: Array.isArray(operatorId) ? operatorId[0] : operatorId,
          };
          await editMessageReSubmissionApi(selectedId, payload);
        }

        resetDialogData();
        onRefresh();
        ToastNotification.success(
          "Message Re‑submission updated successfully!",
        );
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const CheckMessageReSubmission = async (
      data: Partial<MessageReSubmissionType>,
    ) => {
      try {
        const selectedForCheck = Array.isArray(data.operatorId)
          ? data.operatorId
          : data.operatorId != null
            ? [data.operatorId]
            : [];
        const allOperatorsSelectedCheck = areAllOperatorsSelected(
          selectedForCheck,
          operatorsByCountry,
        );

        const baseParams: Partial<
          MessageReSubmissionType & {
            deliveryStat: string;
            connectionProfileId: string;
            vendorId: string;
            destinationOperatorId?: number;
          }
        > = {
          fromDate: dialogData.fromDate,
          toDate: dialogData.toDate,
          deliveryStat: data.dlrStatus,
          connectionProfileId: data.customerId,
          vendorId: data.providerId,
          status: data.status,
          postTranslatedSource: data.postTranslatedSource,
        };

        let summaryResult: MessageReSubmissionSummary;

        if (allOperatorsSelectedCheck) {
          summaryResult = (await CheckMessageReSubmissionAPI(
            baseParams,
          )) as unknown as MessageReSubmissionSummary;
        } else if (selectedForCheck.length <= 1) {
          summaryResult = (await CheckMessageReSubmissionAPI({
            ...baseParams,
            destinationOperatorId: selectedForCheck[0],
          } as Params<MessageReSubmissionType>)) as unknown as MessageReSubmissionSummary;
        } else {
          const responses = await Promise.all(
            selectedForCheck.map((opId) =>
              CheckMessageReSubmissionAPI({
                ...baseParams,
                destinationOperatorId: opId,
              } as Params<MessageReSubmissionType>),
            ),
          );
          summaryResult = responses.reduce<MessageReSubmissionSummary>(
            (acc, r) => {
              const row = r as unknown as MessageReSubmissionSummary;
              return {
                count: acc.count + checkSummaryNumber(row.count),
                delivered: acc.delivered + checkSummaryNumber(row.delivered),
                totalCost: acc.totalCost + checkSummaryNumber(row.totalCost),
                totalRate: acc.totalRate + checkSummaryNumber(row.totalRate),
              };
            },
            { count: 0, delivered: 0, totalCost: 0, totalRate: 0 },
          );
        }

        const { count, delivered, totalCost, totalRate } =
          roundCheckSummary(summaryResult);

        setSummary({ count, delivered, totalCost, totalRate });
        setShowSummary(true);

        ToastNotification.success("Message Re-submission Check successful!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const fieldHistory: IField<
      MessageReSubmissionType & { historyTimestamp?: Date }
    >[] = [
      { key: "name", header: "Name" },
      {
        key: "customerId",
        header: "Customer",
        options: (t) => getCustomerOptions(t.customerId),
      },
      {
        key: "providerId",
        header: "Provider",
        options: (t) => getProviderOptions(t.providerId),
      },
      {
        key: "operatorId",
        header: "Country",
        options: (t) => getOperatorCountryNameOptions(t.operatorId),
      },
      {
        key: "operatorId",
        header: "Operator",
        options: (t) => getOperatorNameOptions(t.operatorId),
      },

      { key: "createdDate", header: "Created Date" },
      { key: "createdBy", header: "User" },
      {
        key: "historyTimestamp",
        header: "Updated At",
      },
      { key: "createdBy", header: "Created By" },
      { key: "updatedBy", header: "Updated By" },
    ];

    return (
      <CustomDialog
        open={dialogOpen}
        onClose={resetDialogData}
        title={
          dialogMode === "history"
            ? "History"
            : isExecuteMode
              ? "Execute"
              : dialogMode === "edit"
                ? "Edit Message Re-Submission"
                : "Message Re-Submission"
        }
        fieldsPerRow={2}
        mode={dialogMode}
        fields={
          dialogMode === "history"
            ? [
                {
                  type: "custom",
                  component: (
                    <History
                      filters={applyFilters}
                      fields={fieldHistory}
                      fetchFunc={({ size = 10 }) =>
                        fetchMessageReSubmissionHistory(String(selectedIds), {
                          size,
                          historyTimestamp: applyFilters.historyTimestamp
                            ? typeof applyFilters.historyTimestamp === "string"
                              ? applyFilters.historyTimestamp
                              : (
                                  applyFilters.historyTimestamp as Date
                                ).toISOString()
                            : undefined,
                        })
                      }
                    />
                  ),
                },
              ]
            : isExecuteMode
              ? [
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Provider"
                        value={dialogData.providerId ?? ""}
                        options={providersOptions}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          setDialogData((prev) => ({
                            ...prev,
                            providerId: String(value),
                          }));
                          validateField("providerId", String(value));
                        }}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.providerId}
                        placeholder="Select Provider"
                        highlightLabelStar={true}
                        helperText={
                          errors.providerId ? "Provider is required" : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  ...(showSummary
                    ? [
                        {
                          type: "custom" as const,
                          fullWidth: true,
                          component: <SummaryStats summary={summary} />,
                        },
                      ]
                    : []),
                ]
              : [
                  {
                    type: "custom" as const,
                    fullWidth: true,
                    component: (
                      <Box width="100%">
                        <CustomDateRangePicker
                          fullWidth
                          fromDate={
                            dialogData.fromDate
                              ? new Date(dialogData.fromDate)
                              : null
                          }
                          setFromDate={(date) =>
                            setDialogData((prev) => ({
                              ...prev,
                              fromDate: date instanceof Date ? date : undefined,
                            }))
                          }
                          toDate={
                            dialogData.toDate
                              ? new Date(dialogData.toDate)
                              : null
                          }
                          setToDate={(date) =>
                            setDialogData((prev) => ({
                              ...prev,
                              toDate: date instanceof Date ? date : undefined,
                            }))
                          }
                        />
                      </Box>
                    ),
                  },
                  {
                    type: "text",
                    label: "Message Re-Submission Name",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: true,
                    value: dialogData.name ?? "",
                    validateOnChange: true,
                    onValidation: (isError) => {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        name: isError,
                      }));
                    },
                    onChange: (value) => {
                      const trimmedValue = String(value).trimStart();
                      setDialogData((prev) => ({
                        ...prev,
                        name: trimmedValue,
                      }));
                    },
                    placeholder: "name",
                    error: !!errors.name,
                    helperText: errors.name ? "Name is required" : "",
                  },
                  {
                    type: "custom",
                    label: "Customer",
                    component: (
                      <AutoComplete
                        label="Customer"
                        value={dialogData.customerId ?? ""}
                        options={clientOptions}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          setDialogData((prev) => ({
                            ...prev,
                            customerId: String(value),
                          }));
                          validateField("customerId", String(value));
                        }}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.customerId}
                        placeholder="Select Customer"
                        highlightLabelStar={true}
                        helperText={
                          errors.customerId ? "Customer is required" : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "select",
                    label: "Status",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: true,
                    value: dialogData.status ?? "",
                    onChange: (
                      value: string | number | (string | number)[],
                    ) => {
                      setDialogData((prev) => ({
                        ...prev,
                        status: String(value),
                      }));
                      validateField("status", String(value));
                    },
                    options: statusOptions,
                    error: !!errors.status,
                    helperText: errors.status ? "Status is required" : "",
                  },
                  {
                    type: "select",
                    label: "DLR Status",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: true,
                    value: dialogData.dlrStatus ?? "",
                    onChange: (
                      value: string | number | (string | number)[],
                    ) => {
                      setDialogData((prev) => ({
                        ...prev,
                        dlrStatus: String(value),
                      }));
                      validateField("dlrStatus", String(value));
                    },
                    options: dlrStatusOptions,
                    error: !!errors.dlrStatus,
                    helperText: errors.dlrStatus
                      ? "DLR Status is required"
                      : "",
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Country"
                        value={dialogData.countryId ?? ""}
                        options={countriesOptions}
                        onChange={handleCountryChange}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.country}
                        placeholder="Select Country"
                        highlightLabelStar={true}
                        helperText={errors.country ? "Country is required" : ""}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Operator"
                        multiple={true}
                        selectAll={true}
                        hideChips={true}
                        value={
                          Array.isArray(dialogData.operatorId)
                            ? dialogData.operatorId.map(String)
                            : []
                        }
                        options={operatorsByCountry}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          const valueArray: number[] = Array.isArray(value)
                            ? value.map((v) => Number(v))
                            : value
                              ? [Number(value)]
                              : [];

                          console.log("Updated operatorId:", valueArray);

                          setDialogData((prev) => ({
                            ...prev,
                            operatorId: valueArray,
                          }));
                        }}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.operatorId}
                        placeholder="Select Operator"
                        highlightLabelStar={true}
                        helperText={
                          errors.operatorId ? "Operator is required" : ""
                        }
                        disabled={isOperatorDisabled}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Provider"
                        value={dialogData.providerId ?? ""}
                        options={providersOptions}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          setDialogData((prev) => ({
                            ...prev,
                            providerId: String(value),
                          }));
                          validateField("providerId", String(value));
                        }}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.providerId}
                        placeholder="Select Provider"
                        highlightLabelStar={true}
                        helperText={
                          errors.providerId ? "Provider is required" : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "text",
                    label: "Translated Sender Id",
                    bgColor: COLORS.WhiteBackground,
                    value: dialogData.postTranslatedSource ?? "",
                    validateOnChange: true,
                    onValidation: (isError) => {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        postTranslatedSource: isError,
                      }));
                    },
                    onChange: (value) => {
                      const trimmedValue = String(value);
                      setDialogData((prev) => ({
                        ...prev,
                        postTranslatedSource: trimmedValue,
                      }));
                    },
                    placeholder: "Post Translated Source",
                    error: false,
                    helperText: "",
                  },
                  ...(showSummary
                    ? [
                        {
                          type: "custom" as const,
                          component: <SummaryStats summary={summary} />,
                        },
                      ]
                    : []),
                ]
        }
        buttons={
          dialogMode === "history"
            ? []
            : isExecuteMode
              ? [
                  {
                    label: "Check",
                    variant: "contained" as ButtonProps["variant"],
                    bgColor: COLORS.GOLD,
                    borderRadius: "12px",
                    onClick: async () => {
                      await CheckMessageReSubmission(
                        dialogData as Partial<MessageReSubmissionType>,
                      );
                    },
                    disabled:
                      isSaveDisabled ||
                      !hasJwtPermission("GET_ALL_RESUBMISSIONS_STATS"),
                  },
                  {
                    label: "Execute",
                    variant: "contained" as ButtonProps["variant"],
                    bgColor: COLORS.GOLD,
                    borderRadius: "12px",
                    onClick: handleExecute,
                    permission: "EXECUTE_RESUBMISSION",
                  },
                ]
              : dialogMode === "edit"
                ? dialogData.resubmissionStatus === "CREATED" ||
                  dialogData.resubmissionStatus === "ERROR"
                  ? [
                      {
                        label: "Check",
                        variant: "contained" as ButtonProps["variant"],
                        bgColor: COLORS.GOLD,
                        borderRadius: "12px",
                        onClick: async () => {
                          await CheckMessageReSubmission(
                            dialogData as Partial<MessageReSubmissionType>,
                          );
                        },
                        disabled:
                          isSaveDisabled ||
                          !hasJwtPermission("GET_ALL_RESUBMISSIONS_STATS"),
                      },
                      {
                        label: "Save",
                        variant: "contained" as ButtonProps["variant"],
                        bgColor: COLORS.GOLD,
                        borderRadius: "12px",
                        onClick: async () => {
                          await handleEditMessageReSubmission(
                            dialogData as Partial<MessageReSubmissionType>,
                          );
                        },
                        disabled:
                          isSaveDisabled ||
                          !hasJwtPermission("PUT_RESUBMISSION"),
                      },
                    ]
                  : [
                      {
                        label: "Check",
                        variant: "contained" as ButtonProps["variant"],
                        bgColor: COLORS.GOLD,
                        borderRadius: "12px",
                        onClick: async () => {
                          await CheckMessageReSubmission(
                            dialogData as Partial<MessageReSubmissionType>,
                          );
                        },
                        disabled:
                          isSaveDisabled ||
                          !hasJwtPermission("GET_ALL_RESUBMISSIONS_STATS"),
                      },
                    ]
                : [
                    {
                      label: "Check",
                      variant: "contained" as ButtonProps["variant"],
                      bgColor: COLORS.GOLD,
                      borderRadius: "12px",
                      onClick: async () => {
                        await CheckMessageReSubmission(
                          dialogData as Partial<MessageReSubmissionType>,
                        );
                      },
                      disabled:
                        isSaveDisabled ||
                        !hasJwtPermission("GET_ALL_RESUBMISSIONS_STATS"),
                    },
                    {
                      label: "Save",
                      variant: "contained" as ButtonProps["variant"],
                      bgColor: COLORS.GOLD,
                      borderRadius: "12px",
                      onClick: async () => {
                        await addMessageReSubmission(
                          dialogData as Partial<MessageReSubmissionType>,
                        );
                      },
                      disabled:
                        isSaveDisabled ||
                        !hasJwtPermission("POST_RESUBMISSION"),
                    },
                  ]
        }
      />
    );
  },
);
export default MessageReSubmissionPage;
